import type { TrackingEvent } from '@/reporting';
import { isInZeloolApp } from '@/utils/tools';

export type PageLeaveTrigger = 'pathname_change' | 'beforeunload';

export interface PageLeaveSnapshotSource {
  location: {
    href: string;
    pathname: string;
    search: string;
  };
  document: {
    referrer: string;
    title: string;
  };
  now: () => number;
}

export interface PageLeaveSnapshot {
  enteredAt: number;
  pageUrl: string;
  urlPath: string;
  referrer: string;
  documentTitle: string;
  sent: boolean;
}

export interface PageLeaveState {
  snapshot: PageLeaveSnapshot | null;
}

export function canTrackSnowplowPageLeave(
  options: {
    hasWindow?: boolean;
    inApp?: boolean;
  } = {},
): boolean {
  const hasWindow = options.hasWindow ?? typeof window !== 'undefined';
  const inApp = options.inApp ?? isInZeloolApp();

  return hasWindow && !inApp;
}

export function createPageLeaveSnapshot(source: PageLeaveSnapshotSource): PageLeaveSnapshot {
  return {
    enteredAt: source.now(),
    pageUrl: source.location.href,
    urlPath: `${source.location.pathname}${source.location.search}`,
    referrer: source.document.referrer || '',
    documentTitle: source.document.title || '',
    sent: false,
  };
}

export function resetPageLeaveState(state: PageLeaveState, source: PageLeaveSnapshotSource): void {
  state.snapshot = createPageLeaveSnapshot(source);
}

export function createPageLeaveEventOnce(
  state: PageLeaveState,
  trigger: PageLeaveTrigger,
  source: PageLeaveSnapshotSource,
  nextUrlPath?: string,
): TrackingEvent | null {
  const snapshot = state.snapshot;

  if (!snapshot || snapshot.sent) {
    return null;
  }

  const viewDuration = Math.max(0, Math.ceil((source.now() - snapshot.enteredAt) / 1000));
  const properties: Record<string, unknown> = {
    page_url: snapshot.pageUrl,
    url_path: snapshot.urlPath,
    referrer: snapshot.referrer,
    document_title: snapshot.documentTitle,
    view_duration: viewDuration,
    leave_trigger: trigger,
  };

  if (trigger === 'pathname_change' && nextUrlPath) {
    properties.next_url_path = nextUrlPath;
  }

  state.snapshot = {
    ...snapshot,
    sent: true,
  };

  return {
    eventName: 'PageLeave',
    properties,
  };
}
